import { create } from 'zustand';
import { useEventComposerStore } from '@/store/useEventComposerStore';

export interface ComposerFields {
  to: string;
  cc: string;
  bcc: string;
  subject: string;
  authoredBody: string;
  accountId: number | null;
}

interface ComposerState {
  isOpen: boolean;
  isMinimized: boolean;
  draftContentId: number | null;
  dirty: boolean;
  fields: ComposerFields;
  openCompose: (prefill?: Partial<ComposerFields> & { draftContentId?: number }) => void;
  closeCompose: () => void;
  minimize: () => void;
  restore: () => void;
  setFields: (patch: Partial<ComposerFields>) => void;
  replaceFields: (fields: Partial<ComposerFields>) => void;
  bindDraft: (draftContentId: number) => void;
  setDirty: (dirty: boolean) => void;
}

const EMPTY_FIELDS: ComposerFields = {
  to: '',
  cc: '',
  bcc: '',
  subject: '',
  authoredBody: '',
  accountId: null,
};

export const useComposerStore = create<ComposerState>((set, get) => ({
  isOpen: false,
  isMinimized: false,
  draftContentId: null,
  dirty: false,
  fields: { ...EMPTY_FIELDS },
  openCompose: (prefill) => {
    const { draftContentId, ...fields } = prefill ?? {};
    const current = get();
    if (current.isOpen && draftContentId !== undefined && current.draftContentId === draftContentId) {
      set({ isMinimized: false });
      return;
    }
    useEventComposerStore.getState().closeEventCompose();
    set({
      isOpen: true,
      isMinimized: false,
      draftContentId: draftContentId ?? null,
      dirty: false,
      fields: { ...EMPTY_FIELDS, ...fields },
    });
  },
  closeCompose: () =>
    set({ isOpen: false, isMinimized: false, draftContentId: null, dirty: false, fields: { ...EMPTY_FIELDS } }),
  minimize: () => set({ isMinimized: true }),
  restore: () => set({ isMinimized: false }),
  setFields: (patch) => set((s) => ({ fields: { ...s.fields, ...patch }, dirty: true })),
  replaceFields: (fields) => set((s) => ({ fields: { ...s.fields, ...fields } })),
  bindDraft: (draftContentId) => set({ draftContentId }),
  setDirty: (dirty) => set({ dirty }),
}));
